import { useCallback, useEffect, useState } from 'react';
import LoginDesarrollos from './LoginDesarrollos';
import OperadorDashboard from './OperadorDashboard';
import { ClienteProvider, useCliente } from './cliente/ClienteContext';
import ClienteIntakeHome from './cliente/ClienteIntakeHome';
import ClientePlaceholder from './cliente/ClientePlaceholder';
import MiAnexo from './cliente/MiAnexo';
import MiPerfil from './cliente/MiPerfil';
import PerfilAduanero from './PerfilAduanero';

// ── SivarSend Desarrollos ───────────────────────────────────────────────────
// Operator side is real Supabase Auth (see LoginDesarrollos). Cliente side
// resolves a real customers row from the last 4 phone digits, but holds no
// Supabase session yet — see cliente/ClienteContext.jsx.
//
// Path routing without a router dependency, same approach as OperadorDashboard:
// pushState to navigate, popstate to follow the back button.
const CLIENT_ROUTES = {
  '/desarrollos/cliente': 'intake',
  '/desarrollos/cliente/mi-anexo': 'mi-anexo',
  '/desarrollos/cliente/intake-manual': 'intake-manual',
  '/desarrollos/cliente/perfil': 'perfil',
  '/desarrollos/cliente/mi-perfil': 'mi-perfil',
};

const DesarrollosShell = () => {
  const { cliente, setCliente } = useCliente();
  const [screen, setScreen] = useState(() =>
    // A refresh on a cliente route with a stored cliente resumes there.
    CLIENT_ROUTES[window.location.pathname] ? 'cliente' : 'login'
  );
  const [path, setPath] = useState(() => window.location.pathname);

  useEffect(() => {
    const onPop = () => setPath(window.location.pathname);
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const navigate = useCallback((to) => {
    window.history.pushState({}, '', to);
    setPath(to);
    if (!CLIENT_ROUTES[to]) setScreen('login');
  }, []);

  const handleClientLogin = useCallback(
    (resolved) => {
      setCliente(resolved);
      setScreen('cliente');
      navigate('/desarrollos/cliente');
    },
    [navigate, setCliente]
  );

  const handleLogout = useCallback(() => {
    setCliente(null);
    setScreen('login');
    navigate('/desarrollos');
  }, [navigate, setCliente]);

  if (screen === 'dashboard') {
    return <OperadorDashboard onLogout={() => setScreen('login')} />;
  }

  if (screen === 'cliente') {
    // Every cliente route needs a resolved cliente. A refresh after the
    // sessionStorage entry is gone lands here, so fall back to the login card
    // rather than rendering a screen with no identity behind it.
    if (!cliente) {
      return (
        <LoginDesarrollos
          onClientLogin={handleClientLogin}
          onOperatorLogin={() => setScreen('dashboard')}
        />
      );
    }

    const route = CLIENT_ROUTES[path] || 'intake';

    if (route === 'perfil') {
      // NOTE: PerfilAduanero is still the UI-only demo component — it renders
      // seeded values and does not read or write desarrollos_perfil_aduanero.
      // Wiring it to the real table is its own brief; until then the gate's
      // CTA lands on a convincing screen that does not persist.
      return (
        <PerfilAduanero
          estado="nuevo"
          onGoToAnexos={() => navigate('/desarrollos/cliente/mi-anexo')}
          onLogout={handleLogout}
        />
      );
    }
    if (route === 'mi-perfil') {
      return <MiPerfil onNavigate={navigate} />;
    }
    if (route === 'mi-anexo') {
      return <MiAnexo onNavigate={navigate} />;
    }
    if (route === 'intake-manual') {
      return (
        <ClientePlaceholder
          title="Formulario manual — próximo release"
          body="Por ahora, usá cualquiera de los otros métodos: foto, factura o nota de voz."
          onBack={() => navigate('/desarrollos/cliente')}
          onNavigate={navigate}
        />
      );
    }
    return <ClienteIntakeHome onNavigate={navigate} onLogout={handleLogout} />;
  }

  return (
    <LoginDesarrollos
      onClientLogin={handleClientLogin}
      onOperatorLogin={() => setScreen('dashboard')}
    />
  );
};

const DesarrollosApp = () => (
  <ClienteProvider>
    <DesarrollosShell />
  </ClienteProvider>
);

export default DesarrollosApp;
